import * as BABYLON from 'babylonjs';
import 'babylonjs-loaders';
import CoreUtils from '../../utils/coreUtils.js';
import Utils3D from '../utils/utils3D.js';
import MeshInfo from './meshInfo';

export const ASSET_TYPE = 
{
	MATERIAL:		1,
	COMPONENT:		2,
	PATH:			3,
	PROCEDURAL:		4,
	TEXTURE:		5,
};

let gAssetCacher = null;
export default class AssetCacher
{
	constructor()
	{
		this.cachedItems = {};
		for(let eType in ASSET_TYPE)
			this.cachedItems[ASSET_TYPE[eType]] = {};

		this.currentlyFetchedUrls = {};

		this.useHighQualityMaterials = false;
		this.freezeMaterials = true;

        const queryString = new URLSearchParams(window.location.search);
		this.loadBakedGltf = queryString.get('bakedGltf') !== 'false';
	}

	static getInstance()
	{
		if(!gAssetCacher)
			gAssetCacher = new AssetCacher();
		return gAssetCacher;
	}

	setUseHighQualityMaterials(bValue)
	{
		if(this.useHighQualityMaterials == bValue)
			return false;

		this.useHighQualityMaterials = bValue;
		this.clearAssetType(ASSET_TYPE.MATERIAL);

		return true;
	}

	setFreezeMaterials(bValue)
	{
		this.freezeMaterials = bValue;
	}

	isLoadingBakedGltf() {
		return this.loadBakedGltf;
	}

    destroy()
	{
        for(const type in ASSET_TYPE)
		{
            const pAllAssets = this.cachedItems[ASSET_TYPE[type]];
            for(const assetId in pAllAssets)
			{
                const pAsset = pAllAssets[assetId];
                if(pAsset && pAsset.dispose)
				{
					if(pAsset.material)
						Utils3D.disposeOfMaterialsAndTextures(pAsset);
                    pAsset.dispose();
				}
            }
            this.cachedItems[ASSET_TYPE[type]] = {};
        }
    }

	clearAssetType(eType)
	{
		this.cachedItems[eType] = {};
	}

	getAssetInfo(pSourceNode, eType, assetId)
	{
		let pAssetInfo = null;
		const pAssetProvider = pSourceNode.getGraph ? pSourceNode.getGraph().getAssetProvider() : window.parametricFixtureAssetProvider;
		if(eType === ASSET_TYPE.COMPONENT)
			pAssetInfo = pAssetProvider.get3DAsset(assetId);
		else if(eType === ASSET_TYPE.MATERIAL)
			pAssetInfo = pAssetProvider.getMaterial(assetId);
		else if(eType === ASSET_TYPE.PROCEDURAL)
			pAssetInfo = pAssetProvider.getNodeGraph(assetId);
		else if(eType === ASSET_TYPE.PATH)
			pAssetInfo = pAssetProvider.getPath(assetId);
		return pAssetInfo;
	}

	getLODFromQuality(eType, id, quality = undefined, pSourceNode = null)
	{
		let lod = undefined;
		if(quality > 1e-4)
		{
			let assetInfo = this.getAssetInfo(pSourceNode, eType, id);
			if(assetInfo['levels_of_detail'])
			{
				const numLods = assetInfo['levels_of_detail'].length;
				lod = CoreUtils.clamp(Math.round((1.0 - quality)*numLods), 0, numLods - 1);
			}
		}

		return lod;
	}

	getAssetCacheKey(eType, id, scene, quality = undefined, pSourceNode = null)
	{
		let lodKeyValue = this.getLODFromQuality(eType, id, quality, pSourceNode);
		if(lodKeyValue === undefined)
			lodKeyValue = 'default';

		let assetCacheKey;
		if(eType === ASSET_TYPE.COMPONENT || eType === ASSET_TYPE.MATERIAL || eType === ASSET_TYPE.TEXTURE)
			assetCacheKey = `${id}-${scene.uid}-${lodKeyValue}`;
		else
			assetCacheKey = id;
		return assetCacheKey;
	}

	getCachedAsset(eType, id, scene, quality = undefined, pSourceNode = null)
	{
		let isValidNode = pSourceNode && pSourceNode.getGraph;
		let bDidCallStart = false;
		// This ain't pretty, but we need this because simply always calling
		// start/finish won't work - despite the promise being returned immediately,
		// it actually queues up an event and resovles then, meaning the code executes
		// later. If we always call start/finish, even when no assets actually need to be
		// loaded, the graphs don't generate because they think they're loading something.
		window.assetManager.ensureAssetInfosLoadedFor(id,
			() => {
				if(!isValidNode)
					return;
				pSourceNode.getGraph().onStartLoadingAsset(id); 
				bDidCallStart = true;
			}
		).then(({numLoaded}) => {
			if(bDidCallStart)
				pSourceNode.getGraph().onFinishLoadingAsset(id);
			if(numLoaded > 0 && isValidNode)
				pSourceNode.markDirty();
		});
		const assetCacheKey = this.getAssetCacheKey(eType, id, scene, quality, pSourceNode);
		return this.cachedItems[eType][assetCacheKey];
	}

	deleteCachedAsset(eType, id, scene, quality = undefined, pSourceNode = null)
	{
		const assetCacheKey = this.getAssetCacheKey(eType, id, scene, quality, pSourceNode);
		delete this.cachedItems[eType][assetCacheKey];
	}

	getOrLoadAsset(eType, assetId, scene, quality, funcRequestStartCallback, funcCallback, pSourceNode = null)
	{
		if(!pSourceNode)
			pSourceNode = `__ac_preload_${assetId}`;

		if(eType === ASSET_TYPE.PROCEDURAL || eType === ASSET_TYPE.PATH)
			this.getJSONAsset(pSourceNode, eType, assetId, funcRequestStartCallback, funcCallback);
		else
			this.getBabylonAsset(pSourceNode, eType, assetId, scene, quality, funcRequestStartCallback, funcCallback);			
	}

	async getJSONAsset(pSourceNode, eType, assetId, funcRequestStartCallback, funcCallback, bPrintWarnings = true)
	{
		if(assetId <= 0 || assetId === undefined)
			return false;

		let bDidLoadAssetInfoRemotely = false;
		if(!window.assetManager.getById(assetId))
		{
			bDidLoadAssetInfoRemotely = true;
			if(pSourceNode.getGraph)
				pSourceNode.getGraph().onStartLoadingAsset(assetId);
			if(funcRequestStartCallback)
				funcRequestStartCallback();
			await window.assetManager.ensureAssetInfosLoadedFor(assetId);
		}
		let cachedAsset = this.getCachedAsset(eType, assetId);
		if(cachedAsset)
		{
			if(funcRequestStartCallback && !bDidLoadAssetInfoRemotely)
				funcRequestStartCallback();

			if(funcCallback)
				funcCallback(cachedAsset);
			if(bDidLoadAssetInfoRemotely && pSourceNode.getGraph)
				pSourceNode.getGraph().onFinishLoadingAsset(assetId);
			return true;
		}

		let pAssetInfo = this.getAssetInfo(pSourceNode, eType, assetId);
		if(!pAssetInfo)
		{
			if(bPrintWarnings)
				console.warn(`Unable to find asset info for JSON asset id = ${assetId}`);
			if(bDidLoadAssetInfoRemotely)
			{
				if(funcCallback)
					funcCallback(null);
				if(pSourceNode.getGraph)
					pSourceNode.getGraph().onFinishLoadingAsset(assetId);
			}
			return false;
		}

		this.fetchJSONInternal(pSourceNode, pAssetInfo['file'], 
			() => {
				if(!bDidLoadAssetInfoRemotely)
				{
					if(pSourceNode.getGraph && !bDidLoadAssetInfoRemotely)
						pSourceNode.getGraph().onStartLoadingAsset(assetId);
					if(funcRequestStartCallback)
						funcRequestStartCallback();
				}
			}, 
			(jsonData) => {
				this.storeAsset(eType, assetId, jsonData);
				if(funcCallback)
					funcCallback(jsonData);
				if(pSourceNode.getGraph)
					pSourceNode.getGraph().onFinishLoadingAsset(assetId);
			});
		return true;
	}

	async getBabylonAsset(pSourceNode, eType, assetId, scene, quality, funcRequestStartCallback, funcCallback)
	{
		if(assetId <= 0 || assetId === undefined)
			return;

		let bDidLoadAssetInfoRemotely = false;
		if(!window.assetManager.getById(assetId))
		{
			bDidLoadAssetInfoRemotely = true;
			if(pSourceNode.getGraph)
				pSourceNode.getGraph().onStartLoadingAsset(assetId);
			if(funcRequestStartCallback)
				funcRequestStartCallback();
			await window.assetManager.ensureAssetInfosLoadedFor(assetId);
		}
		let cachedAsset = this.getCachedAsset(eType, assetId, scene, quality, pSourceNode);
		if(cachedAsset)
		{
			if(funcRequestStartCallback && !bDidLoadAssetInfoRemotely)
				funcRequestStartCallback();

			if(funcCallback)
				funcCallback(cachedAsset);
			if(pSourceNode.getGraph && bDidLoadAssetInfoRemotely)
				pSourceNode.getGraph().onFinishLoadingAsset(assetId);
			return;
		}

		let pAssetInfo = this.getAssetInfo(pSourceNode, eType, assetId);
		if(!pAssetInfo)
		{
			console.warn(`Unable to find asset info for 3D asset id = ${assetId}`);
			if(bDidLoadAssetInfoRemotely)
			{
				if(funcCallback)
					funcCallback(eType === ASSET_TYPE.MATERIAL ? null : []);
				if(pSourceNode.getGraph)
					pSourceNode.getGraph().onFinishLoadingAsset(assetId);
			}
			return;
		}
		let fileName = pAssetInfo['file'];

		let lodValue = this.getLODFromQuality(eType, assetId, quality, pSourceNode);
		if(pAssetInfo['levels_of_detail'])
		{
			if(lodValue === undefined)
				lodValue = AssetCacher.getInstance().getLODLevel(eType === ASSET_TYPE.MATERIAL, pAssetInfo['levels_of_detail']);

			if(pAssetInfo['levels_of_detail'][lodValue]) {
				const lodFiles = pAssetInfo['levels_of_detail'][lodValue].files;
				fileName = lodFiles['gltf'];

				if(AssetCacher.getInstance().isLoadingBakedGltf() && lodFiles['gltf_baked'] !== undefined) {
					fileName = lodFiles['gltf_baked'];
				}
			}
		}
		
		this.fetchBabylonFileInternal(pSourceNode, assetId, fileName, scene, lodValue,
			() => {
				if(!bDidLoadAssetInfoRemotely)
				{
					if(pSourceNode.getGraph)
						pSourceNode.getGraph().onStartLoadingAsset(assetId);
					if(funcRequestStartCallback)
						funcRequestStartCallback();
				}
			}, 
			(loadedMeshes) => {
				if(loadedMeshes && loadedMeshes.length > 0)
					this.storeAsset(eType, assetId, loadedMeshes, quality, pSourceNode);
				if(funcCallback)
					funcCallback((loadedMeshes && eType === ASSET_TYPE.MATERIAL) ? loadedMeshes[0] : loadedMeshes);
				if(pSourceNode.getGraph)
					pSourceNode.getGraph().onFinishLoadingAsset(assetId);
			});
	}

	applyColorMaterialParams(targetMaterial, color) {
		// TODO: Verify how custom colors render and check whether we should do gamma correction
		// or not.
		targetMaterial.albedoColor = CoreUtils.applyGammaCorrection(color, 2.2);
		targetMaterial.metallic = 0.01;
		targetMaterial.roughness = 1.0;
	}

	getColorMaterialAsset(color, scene)
	{
		const colorKey = color.toHexString();
		const material = new BABYLON.PBRMaterial(`assetcache_color_mat_${colorKey}`, scene);
	
		this.applyColorMaterialParams(material, color);

		if(this.freezeMaterials)
			Utils3D.freezeMaterials(material);

		// NOTE: This is not great structurally, because we normally store a basic cube with
		// a material applied to it in the materials cache. This mixes in some cases where we
		// store just the material, so we're now inconsistent. For that reason, we also can't
		// use this.storeAsset() here, which we should.
		// Ideally, we should only store material objects in the future, but that involves other
		// changes in the node graph.
		const id = this.getAssetCacheKey(ASSET_TYPE.MATERIAL, colorKey, scene);
		this.cachedItems[ASSET_TYPE.MATERIAL][id] = material;
		return material;
	}

	storeAsset(eType, id, pAssetOrList, quality = undefined, pSourceNode = null)
	{
		if(!pAssetOrList)
			return;

		let assetList = pAssetOrList;
		if(!Array.isArray(pAssetOrList))
			assetList = [pAssetOrList];

		const scene = assetList[0]?.getScene ? assetList[0]?.getScene() : undefined;
		const storageId = this.getAssetCacheKey(eType, id, scene, quality, pSourceNode);
		if(this.cachedItems[eType][storageId])
			return;

		// For all but a component type, we only cache a single item. For components,
		// we cache lists of meshes.
		let itemToCache = assetList[0];
		if(eType === ASSET_TYPE.COMPONENT || eType === ASSET_TYPE.MATERIAL)
		{
			assetList.forEach((pAsset) => {
				Utils3D.unparentAndPreserveTransform(pAsset);
				pAsset.isCached = true;
				pAsset.setEnabled(false);

				if(pAsset.name.indexOf('assetcache_') < 0)
					pAsset.name = `assetcache_${id}_` + pAsset.name;
						
				let strNewRootName = pAsset.name + CoreUtils.createGuid();
				pAsset.id = strNewRootName;

				if(pAsset.material.subMaterials)
					pAsset.material.subMaterials.forEach((sm) => {
						if(sm.name.indexOf('assetcache_') < 0)
							sm.name = `assetcache_${id}_` + sm.name;
					});

				if(pAsset.material)
					pAsset.material.name = `assetcache_${id}_` + pAsset.material.name;

				// Check num verts
				const numVertices = pAsset.getTotalVertices();
				if(eType === ASSET_TYPE.COMPONENT && numVertices >= 35000)
				{
					// Compute our voxel grid for fast intersection testing
					if(numVertices > 500000)
						console.warn(`Mesh with ${numVertices} vertices being loaded for asset id = ${id}. This will degrade performance. Storage id = ${storageId}`);
					pAsset.localVoxelGrid = MeshInfo.generateLocalFaceVoxelGrid(pAsset);
				}
			});

			if(eType === ASSET_TYPE.COMPONENT)
				itemToCache = assetList;
		}

		this.cachedItems[eType][storageId] = itemToCache;

		// If the quality is specified, also store a copy of this item in the default value key
		// so that callers who don't provide a quality get whatever is currently loaded:
		if(quality !== undefined)
		{
			const defaultStorageId = this.getAssetCacheKey(eType, id, scene, undefined, pSourceNode);
			if(!this.cachedItems[eType][defaultStorageId])
				this.cachedItems[eType][defaultStorageId] = itemToCache;
		}
	}

	getMapKey(pSourceNodeOrId)
	{
		if(typeof pSourceNodeOrId === "string")
			return pSourceNodeOrId;
		else
			return `${pSourceNodeOrId.getGraph().getGraphId()}_${pSourceNodeOrId.getId()}`;
	}

	getIsNodeLoading(sourceNode)
	{
		let strMapId = this.getMapKey(sourceNode);
		for(let fullUrl in this.currentlyFetchedUrls)
		{
			if(this.currentlyFetchedUrls[fullUrl][strMapId])
				return true;
		}

		return false;
	}

	getIsAssetLoading(strFullUrl)
	{
		return this.currentlyFetchedUrls[strFullUrl];
	}

	clearCallbacksStartingWith(strKeyStart)
	{
		// We run into cases where during the callbacks below,
		// the caller attempts to load a graph (even if it's a null data object).
		// In doing so, we attempt to reset the scene and end up calling this function
		// again, resulting in an infinite call stack overflow.
		if(this.isClearingCallbacks)
			return;
		this.isClearingCallbacks = true;
		for(let strUrl in this.currentlyFetchedUrls)
		{
			const subkeys = Object.keys(this.currentlyFetchedUrls[strUrl]);
			for(let subkey of subkeys)
			{
				if(subkey.indexOf(strKeyStart) === 0)
				{
					let funcStoredCallback = this.currentlyFetchedUrls[strUrl][subkey];
					funcStoredCallback(null);
					delete this.currentlyFetchedUrls[strUrl][subkey];
				}
			}
		}
		this.isClearingCallbacks = false;
	}

	getLODLevel(bIsLoadingMaterial, lods)
	{
		let lodIndex = 0;
		if(lods)
		{
			if(bIsLoadingMaterial)
			{
				if(this.useHighQualityMaterials)
					lodIndex = Math.floor(lods.length*0.25);
				else
					lodIndex = Math.floor(lods.length*0.5);
			}
			else
			{
				if(lods.length > 3)
					lodIndex = Math.floor(lods.length*0.75);
				else
					lodIndex = lods.length - 1;
			}
		}
		return lodIndex;
	}

	processFetchedUrls(strFullUrl, data)
	{
		// We must delete first before running our callbacks, otherwise,
		// the clearing of the scene will call clear calls in progress,
		// it'll think we're still in progress, and so will make the
		// finished callback twice.
		let oldFetchedUrls = this.currentlyFetchedUrls[strFullUrl];
		delete this.currentlyFetchedUrls[strFullUrl];
		for(let nodeId in oldFetchedUrls)
		{
			let funcStoredCallback = oldFetchedUrls[nodeId];
			funcStoredCallback(data);
		}
	}

	fetchJSONInternal(pSourceNodeOrId, strFullUrl, funcRequestStartCallback, funcCallback)
	{
		let strMapId = this.getMapKey(pSourceNodeOrId);
		if(funcRequestStartCallback && (!this.currentlyFetchedUrls[strFullUrl] || !this.currentlyFetchedUrls[strFullUrl][strMapId]))
			funcRequestStartCallback();

		// Check that we're not already fetching this URL:
		if(!this.currentlyFetchedUrls[strFullUrl])
		{
			// Start fetching
			this.currentlyFetchedUrls[strFullUrl] = {};
			fetch(strFullUrl)
				.then((data) => { return data.json(); })
				.then((jsonData) =>
				{
					this.processFetchedUrls(strFullUrl, jsonData);				
				})
				.catch((error) =>
				{
					this.processFetchedUrls(strFullUrl, null);
				});
		}

		// Append the info here, but only if we don't have the same node asking for it already:
		if(!this.currentlyFetchedUrls[strFullUrl][strMapId])
			this.currentlyFetchedUrls[strFullUrl][strMapId] = funcCallback;
	}

	fetchBabylonFileInternal(pSourceNodeOrId, assetId, strFullUrl, scene, specificLOD, funcRequestStartCallback, funcCallback)
	{
		let strMapId = this.getMapKey(pSourceNodeOrId);
		if(funcRequestStartCallback && (!this.currentlyFetchedUrls[strFullUrl] || !this.currentlyFetchedUrls[strFullUrl][strMapId]))
			funcRequestStartCallback();

		// Check that we're not already fetching this URL:
		if(!this.currentlyFetchedUrls[strFullUrl])
		{
			// Start fetching
			this.currentlyFetchedUrls[strFullUrl] = {};

			const {path, filename} = CoreUtils.splitFullFilename(strFullUrl);

			if(filename.indexOf('.gltf') > 0)
			{
				let detailedAssetInfo = window.assetManager.getById(assetId) || [];

				CoreUtils.log(`Asset ${assetId} geo: # of LODs is ${detailedAssetInfo['levels_of_detail']?.length}, using ${specificLOD}`);

				// Now that we don't need pre-signed URLs, we can use the same policy and key pair to ensure
				// we can load any files from S3:
				let authDetails = '';
				const policyIdx = filename.indexOf('Policy=');
				if(policyIdx > 0)
					authDetails = filename.substr(policyIdx);

				let gltfData = null;

				let gltfLoader = BABYLON.SceneLoader.LoadAssetContainer(path, filename, scene, (importedAssetsContainer) => {
					if(importedAssetsContainer.meshes.length === 0)
					{
						// For our imported materials, we actually just store materials now, without meshes.
						// However, the entire rest of the code is predicated on BJS file workflow, where materials
						// are stored on a mesh. So we create a dummy box and assign the first material to it.
						let dummyMesh = BABYLON.MeshBuilder.CreateBox(`_dummy_mat_${filename}`, { 
							width: 0.25,
							depth: 0.25,
							height: 0.25,
							sideOrientation: BABYLON.Mesh.FRONTSIDE,
						}, scene);
						if (importedAssetsContainer.materials.length > 0) {
							const material = importedAssetsContainer.materials[0];
							// Colors in GLTF are in sRGB space now, so no gamma correction needed.
							/// material['albedoColor'] = CoreUtils.applyGammaCorrection(material['albedoColor']);

							if (
								(gltfData.materials.length === 1)
								&& ('extensions' in gltfData.materials[0])
								&& ('KHR_materials_transmission' in gltfData.materials[0].extensions)
							) {
								material.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;
								material.alpha = 1 - gltfData.materials[0].extensions.KHR_materials_transmission.transmissionFactor;
							}

							dummyMesh.material = material;
						}
						importedAssetsContainer.meshes.push(dummyMesh);
					}

					importedAssetsContainer.meshes.forEach((mesh) => {
						// BJS glTF importer basically rotates by 180 and flips Z
						// on import. Here, we undo the rotation (but keep the Z flip)
						if(!mesh.parent)
						{
							mesh.scaling.x *= -1;
							mesh.scaling.z *= -1;
						}
					});

					this.processLoadedMeshes(importedAssetsContainer.meshes, strFullUrl);
				}, null, (bjsScene, message, exception) => {
					// TODO: Handle error loading assets here so we don't block the entire project
					console.log(`Error loading GLTF asset: ${message}`);
				});

				gltfLoader.loadAllMaterials = true;
				gltfLoader.onParsed = (data) =>
				{
					gltfData = data.json;

					let materialsData = data.json.materials;
					try
					{
						for(let material of materialsData)
							delete material['normalTexture'];
					}
					catch(error)
					{

					}
				};
				gltfLoader.preprocessUrlAsync = async (referencedUrl) => {
					
					const baseFileName = referencedUrl.substring(referencedUrl.lastIndexOf('/') + 1, referencedUrl.length);

					let mapInfo = null;
					let iMapIndex;
					let numLods = detailedAssetInfo['levels_of_detail'].length;
					for(let iLod = 0; !mapInfo && iLod < numLods; iLod++)
					{
						let mapInfos = detailedAssetInfo['levels_of_detail'][iLod];
						if(mapInfos['maps'])
							mapInfos = mapInfos['maps'];
						for(iMapIndex = 0; iMapIndex < mapInfos.length; iMapIndex++)
						{
							if(mapInfos[iMapIndex].files.jpg.includes(baseFileName))
							{
								mapInfo = mapInfos[iMapIndex];
								break;
							}
						}
					}

					if(!mapInfo)
						return Promise.resolve(referencedUrl + '?' + authDetails);

					const bIsLoadingMaterial = detailedAssetInfo['asset_type'] == 1;
					let lodIndex = specificLOD === undefined ? this.getLODLevel(bIsLoadingMaterial, detailedAssetInfo['levels_of_detail']) : specificLOD;

					if(mapInfo.parameter_type == 'metallic' || mapInfo.parameter_type == 'roughness')
						lodIndex = numLods - 1;
					let mapInfos = detailedAssetInfo['levels_of_detail'][lodIndex];
					if(mapInfos['maps'])
						mapInfos = mapInfos['maps'];
					let newName = mapInfos[iMapIndex].files.jpg;
					if(!newName)
					{
						console.warn(`Unable to find mapped glTF texture file name, using the first one.`);
						newName = jpgFiles[0];
					}
					return Promise.resolve(newName);
				}
			}
			else
			{
				BABYLON.SceneLoader.ForceFullSceneLoadingForIncremental = true;
				BABYLON.SceneLoader.ImportMesh(null, path, filename, scene, (newMeshes) => 
				{
					this.processLoadedMeshes(newMeshes, strFullUrl);
				},
				null,
				(bjsScene, message, exception) => 
				{
					this.processFetchedUrls(strFullUrl, null);
				});
			}
		}

		// Append the info here, but only if we don't have the same node asking for it already:
		if(!this.currentlyFetchedUrls[strFullUrl][strMapId])
			this.currentlyFetchedUrls[strFullUrl][strMapId] = funcCallback;
	}

	processLoadedMeshes(newMeshes, strFullUrl)
	{
		let usableMeshes = [];
		let pDisposeList = [];
		newMeshes.forEach((pMesh) =>
		{
			if(pMesh.parent || newMeshes.length === 1)
				usableMeshes.push(pMesh);
			else
				pDisposeList.push(pMesh);
		});

		const funcGoThroughCallbacksAndCleanup = () => {

			// We need to make sure every mesh is unparented. Usable meshes will contain
			// a single mesh if there's a single root node and one part underneath it,
			// so it can't be a part of the if statement below.
			let origRotation = null;
			usableMeshes.forEach((mesh) => { 
				Utils3D.unparentAndPreserveTransform(mesh);
				if(!origRotation)
				{
					 let endRot = BABYLON.Quaternion.FromEulerAngles(0, Math.PI, Math.PI);
					 let postConv1 = BABYLON.Quaternion.FromEulerAngles(mesh.rotation.x, mesh.rotation.y, mesh.rotation.z);
					 origRotation = postConv1.invert().multiply(endRot.invert());
				}
			});

			// If we have more than one mesh, as may happen with primitives
			// in GLTF, combine them:
			// NOTE on the above: we need to run through this combine logic
			// anyway, because it also does something to the transformations
			// where the meshes that don't go through it are flipped upside-down
			// (such as many accessories). We'll fix it properly if/when we need
			// to support uncombined meshes.

			// We support lists of meshes now, so skip merging them for now.
			// We might come back to this for optimizations. Notice the last
			// true parameter which forces part materials to be preserved.
			// New note: There appears to be a bug in MergeMeshes() which incorrectly
			// orients meshes specifically when there's only one being converted.
			// So, we reset the rotations, and then restore them after the fact.
			// We also need to flip faces because apparently that is what's going on
			// inside.
			let resMesh = BABYLON.Mesh.MergeMeshes(usableMeshes, true, true, null, false, true);
			resMesh.origRotationQuaternion = origRotation;
			if(usableMeshes.length === 1)
				resMesh.flipFaces(false);

			if(this.freezeMaterials)
				Utils3D.freezeMaterials(resMesh.material);

			this.processFetchedUrls(strFullUrl, [resMesh]);

			pDisposeList.forEach((pMesh) => { pMesh.dispose(); });
		};

		// We have to wait until our textures have finished loading, too.
		let textureLoadCount = 0;
		usableMeshes.forEach((pMesh) => {
			if(!pMesh.material)
				return;

			let materials = pMesh.material.subMaterials ? pMesh.material.subMaterials : [pMesh.material];
			for(let currMaterial of materials)
			{
				for(let propertyName of Object.keys(currMaterial))
				{
					const texture = currMaterial[propertyName];
					if(!(texture instanceof BABYLON.Texture))
						continue;

					if(!texture || texture.isReadyOrNotBlocking())
						continue;

				textureLoadCount++;
					texture.onLoadObservable.addOnce(() => { 
					textureLoadCount--;
					if(textureLoadCount === 0)
						funcGoThroughCallbacksAndCleanup();
				});
			}
			}
		});

		if(textureLoadCount === 0)
			funcGoThroughCallbacksAndCleanup();
	}

    ensureAssetsLoaded(assetIdList, scene, callback) {
        
        window.assetManager.ensureAssetInfosLoadedFor(assetIdList).then(({assetInfos}) => {
			// We need to use this and not the original list because if there is an invalid
			// asset id passed in, we'll never call the callback otherwise:
			let assetIds = Object.keys(assetInfos);
			let iLoadingAssetsCount = assetIds.length;
			for(let assetId of assetIds)
			{
				let assetInfo = assetInfos[assetId];
				// NOTE that we're loading at default quality 0.5, because that's what gets used
				// for fixtures and props. If the numbers there change (DEFAULT_FIXTURE_QUALITY and
				// DEFAULT_PROP_QUALITY) we'll need to update this, too.
				const quality = (assetInfo.asset_type == ASSET_TYPE.COMPONENT) ? 0.5 : undefined;
				this.getOrLoadAsset(assetInfo.asset_type, assetId, scene, quality, null,
					() => {
						iLoadingAssetsCount--;
						if(iLoadingAssetsCount === 0)
						{
							// We're done:
							if(callback)
								callback();
						}
					});
			}

			if(assetIds.length === 0 && callback)
				callback();
		});
    }
}
